function finite(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

export function projectionsFromApiRows(rows, { taxRate = null } = {}) {
    if (!Array.isArray(rows)) return [];
    const rate = finite(taxRate);
    const canReconcileTaxes = rate != null && rate >= 0 && rate < 1;

    return rows.flatMap((row) => {
        const year = finite(row?.year);
        const ebit = finite(row?.ebit);
        const depreciation = finite(row?.d_and_a);
        const taxes = finite(row?.taxes);
        const capex = finite(row?.capex);
        const deltaNwc = finite(row?.delta_nwc);
        const fcff = finite(row?.fcff);
        if (year == null || ebit == null || depreciation == null || taxes == null || capex == null || deltaNwc == null) {
            return [];
        }

        const revenue = finite(row?.revenue);
        if (fcff == null || !canReconcileTaxes) {
            return [{
                year,
                revenue,
                ebitda: fcff == null ? ebit + depreciation : fcff + taxes + capex + deltaNwc,
                depreciation,
                taxes,
                capex,
                deltaNwc,
            }];
        }

        const ebitda = (fcff - depreciation * rate + capex + deltaNwc) / (1 - rate);
        return [{
            year,
            revenue,
            ebitda,
            depreciation,
            taxes: (ebitda - depreciation) * rate,
            capex,
            deltaNwc,
        }];
    });
}

function unleveredTaxes(entry) {
    const ebit = finite(entry?.operating_income);
    const pretax = finite(entry?.pretax_income);
    const incomeTax = finite(entry?.income_tax);
    if (ebit == null || pretax == null || pretax === 0 || incomeTax == null) return null;
    return ebit * (incomeTax / pretax);
}

export function actualsFromHistory(history) {
    if (!Array.isArray(history)) return [];
    const rows = history.flatMap((entry) => {
        const year = finite(entry?.year);
        if (year == null) return [];
        const ebit = finite(entry.operating_income);
        const depreciation = finite(entry.d_and_a);
        return [{
            year,
            revenue: finite(entry.revenue),
            ebitda: ebit != null && depreciation != null ? ebit + depreciation : null,
            depreciation,
            taxes: unleveredTaxes(entry),
            capex: finite(entry.capex),
            operatingNwc: finite(entry.operating_nwc),
        }];
    }).sort((left, right) => left.year - right.year);

    return rows.map((row, index) => {
        const previous = rows[index - 1];
        const priorNwc = finite(previous?.operatingNwc);
        const deltaNwc = previous
            && row.year === previous.year + 1
            && row.operatingNwc != null
            && priorNwc != null
            ? row.operatingNwc - priorNwc
            : null;
        return {
            year: row.year,
            revenue: row.revenue,
            ebitda: row.ebitda,
            depreciation: row.depreciation,
            taxes: row.taxes,
            capex: row.capex,
            deltaNwc,
        };
    });
}

export function statementAmounts(row) {
    const ebitda = finite(row?.ebitda);
    const depreciation = finite(row?.depreciation);
    const taxes = finite(row?.taxes);
    const capex = finite(row?.capex);
    const deltaNwc = finite(row?.deltaNwc);
    const complete = [ebitda, taxes, capex, deltaNwc].every((value) => value != null);
    return {
        ebit: ebitda != null && depreciation != null ? ebitda - depreciation : null,
        depreciationAddBack: depreciation,
        unleveredFreeCashFlow: complete ? ebitda - taxes - capex - deltaNwc : null,
    };
}

export function midYearDiscountFactor(year, wacc) {
    return 1 / ((1 + wacc) ** (year - 0.5));
}

export function yearEndDiscountFactor(year, wacc) {
    return 1 / ((1 + wacc) ** year);
}

export function calculateExitMultipleValuation({
    projections,
    wacc,
    exitMultiple,
    totalDebt,
    preferredStock,
    noncontrollingInterest,
    cash,
    sharesOutstanding,
}) {
    const rate = finite(wacc);
    const multiple = finite(exitMultiple);
    const debt = finite(totalDebt);
    const preferred = finite(preferredStock);
    const noncontrolling = finite(noncontrollingInterest);
    const cashValue = finite(cash);
    const shares = finite(sharesOutstanding);
    const validRate = rate != null && rate > -1;
    const validMultiple = multiple != null;
    const validCapital = debt != null && preferred != null && noncontrolling != null && cashValue != null;

    const rows = (Array.isArray(projections) ? projections : []).map((projection) => {
        const year = finite(projection?.year);
        const ebitda = finite(projection?.ebitda);
        const depreciation = finite(projection?.depreciation);
        const taxes = finite(projection?.taxes);
        const capex = finite(projection?.capex);
        const deltaNwc = finite(projection?.deltaNwc);
        const inputsValid = [year, ebitda, depreciation, taxes, capex, deltaNwc].every((value) => value != null);
        const discountFactor = inputsValid && validRate ? midYearDiscountFactor(year, rate) : null;
        const unleveredFreeCashFlow = inputsValid ? ebitda - taxes - capex - deltaNwc : null;
        return {
            year,
            revenue: finite(projection?.revenue),
            ebitda,
            depreciation,
            taxes,
            capex,
            deltaNwc,
            ebit: inputsValid ? ebitda - depreciation : null,
            depreciationAddBack: inputsValid ? depreciation : null,
            unleveredFreeCashFlow,
            discountPeriod: year == null ? null : year - 0.5,
            discountFactor,
            presentValue: discountFactor != null && unleveredFreeCashFlow != null
                ? unleveredFreeCashFlow * discountFactor
                : null,
        };
    });

    const complete = rows.length > 0
        && rows.every((row) => row.presentValue != null)
        && validRate
        && validMultiple;
    const cumulativePresentValue = complete
        ? rows.reduce((sum, row) => sum + row.presentValue, 0)
        : null;
    const finalRow = complete ? rows[rows.length - 1] : null;
    const terminalYearEbitda = finalRow ? finalRow.ebitda : null;
    const terminalValue = finalRow ? terminalYearEbitda * multiple : null;
    const terminalDiscountFactor = finalRow ? yearEndDiscountFactor(finalRow.year, rate) : null;
    const presentValueOfTerminalValue = finalRow ? terminalValue * terminalDiscountFactor : null;
    const enterpriseValue = complete ? cumulativePresentValue + presentValueOfTerminalValue : null;
    const equityValue = complete && validCapital
        ? enterpriseValue - debt - preferred - noncontrolling + cashValue
        : null;

    return {
        rows,
        cumulativePresentValue,
        terminalYearEbitda,
        exitMultiple: validMultiple ? multiple : null,
        terminalValue,
        terminalDiscountFactor,
        presentValueOfTerminalValue,
        terminalShareOfEnterpriseValue: enterpriseValue
            ? presentValueOfTerminalValue / enterpriseValue
            : null,
        enterpriseValue,
        totalDebt: debt,
        preferredStock: preferred,
        noncontrollingInterest: noncontrolling,
        cash: cashValue,
        equityValue,
        sharesOutstanding: shares,
        sharePrice: equityValue != null && shares != null && shares > 0 ? equityValue / shares : null,
    };
}

export function exitMultipleSensitivity(inputs) {
    const baseWacc = finite(inputs?.wacc);
    const baseMultiple = finite(inputs?.exitMultiple);
    const projections = inputs?.projections;
    if (!Array.isArray(projections) || projections.length === 0 || baseWacc == null || baseMultiple == null) return [];

    const cells = [];
    for (let waccOffset = -2; waccOffset <= 2; waccOffset += 1) {
        for (let multipleOffset = -2; multipleOffset <= 2; multipleOffset += 1) {
            const wacc = baseWacc + waccOffset * 0.01;
            const exitMultiple = baseMultiple + multipleOffset;
            const valuation = calculateExitMultipleValuation({
                ...inputs,
                wacc,
                exitMultiple,
            });
            cells.push({
                wacc,
                exitMultiple,
                isCurrent: waccOffset === 0 && multipleOffset === 0,
                sharePrice: valuation.sharePrice,
            });
        }
    }
    return cells;
}
